import { ExactDecimal, currencyDigits } from './money';
export const STORE_TIME_ZONE = 'Asia/Ho_Chi_Minh';
export function money(value: number, currency = 'VND', locale = 'vi-VN') {
  return exactMoney(value, currency, locale);
}
export function exactMoney(value: string | number, currency = 'VND', locale = 'vi-VN') {
  if ((typeof value === 'number' && !Number.isFinite(value)) || !/^[+-]?[0-9.eE]+$/.test(String(value))) return '—';
  let exact: ExactDecimal;
  try { exact = ExactDecimal.from(value); } catch { return '—'; }
  const negative = exact.units < 0n;
  const [integer, fraction = ''] = exact.toString().replace(/^-/, '').split('.');
  const digits = fraction.padEnd(currency === 'XXX' ? 0 : currencyDigits(currency), '0');
  const formatter = new Intl.NumberFormat(locale, { ...(currency === 'XXX' ? {} : { style: 'currency' as const, currency }), minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const integral = BigInt(integer), signed = negative ? (integral === 0n ? -0 : -integral) : integral;
  const parts = formatter.formatToParts(signed);
  const end = parts.reduce((last, part, index) => ['integer', 'group'].includes(part.type) ? index : last, -1);
  if (digits) {
    const decimal = new Intl.NumberFormat(locale, { minimumFractionDigits: 1 }).formatToParts(1.1).find(part => part.type === 'decimal')?.value || '.';
    const localDigits = Array.from({ length: 10 }, (_, index) => new Intl.NumberFormat(locale, { useGrouping: false }).format(index));
    parts.splice(end + 1, 0, { type: 'decimal', value: decimal }, { type: 'fraction', value: digits.replace(/[0-9]/g, digit => localDigits[Number(digit)]) });
  }
  return parts.map(part => part.value).join('');
}
export function compactMoney(value: number, currency = 'VND', locale = 'vi-VN') {
  return money(value, currency, locale);
}
export function timeLabel(ms: number, timeZone = STORE_TIME_ZONE, locale = 'vi-VN') {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone, hourCycle: 'h23' }).format(ms);
}
// Machine-readable HH:mm for time inputs and calendar arithmetic. Never parse
// localized display digits (Arabic, Bengali, etc.) back into a number.
export function clockKey(ms: number, timeZone = STORE_TIME_ZONE) {
  return new Intl.DateTimeFormat('en-GB-u-nu-latn', { hour: '2-digit', minute: '2-digit', timeZone, hourCycle: 'h23' }).format(ms);
}
export function dateKey(ms: number, timeZone = STORE_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(ms);
  const get = (name: string) => parts.find(p => p.type === name)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}
export function addDays(date: string, days: number) {
  const d = new Date(date + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10);
}
export function weekStart(date: string) {
  const day = new Date(date + 'T12:00:00Z').getUTCDay(); return addDays(date, -(day === 0 ? 6 : day - 1));
}
export function dateLabel(date: string, options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }, locale = 'vi-VN') {
  // Already a store-calendar date, not an instant: never shift it across zones.
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(new Date(date + 'T12:00:00Z'));
}
// Calendar date boundaries in any store timezone, independent of the browser timezone.
export function startOfStoreDay(date: string, timeZone = STORE_TIME_ZONE) {
  const target = Date.parse(date + 'T00:00:00Z');
  let guess = target;
  for (let i = 0; i < 3; i++) {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(guess);
    const get = (k: string) => Number(parts.find(p => p.type === k)?.value);
    const represented = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    guess += target - represented;
  }
  return guess;
}
export function initials(name: string) { return name.split(' ').filter(Boolean).slice(-2).map(p => p[0]).join('').toUpperCase(); }
export function matches(value: string, query: string) {
  const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
  return normalize(value).includes(normalize(query));
}
